import { useTranslation } from '@multaqa/i18n'
import { Alert, AlertDescription, AlertTitle } from '@multaqa/ui/components/ui/alert'
import { Button } from '@multaqa/ui/components/ui/button'
import { EmptyState } from '@multaqa/ui/components/ui/empty-state'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@multaqa/ui/components/ui/sheet'
import { HistoryIcon } from 'lucide-react'
import { useState } from 'react'
import { useCompanyAudit } from '@/features/audit/api'
import { AuditCard } from '@/features/audit/audit-card'
import { AuditDetailSheet } from '@/features/audit/audit-detail-sheet'
import { groupAuditRows } from '@/features/audit/audit-utils'

export type RecordHistoryPanelProps = {
  recordType: string
  recordId: string
  title?: string
  triggerLabel?: string
}

export function RecordHistoryPanel({
  recordType,
  recordId,
  title,
  triggerLabel,
}: RecordHistoryPanelProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<
    import('@/features/audit/api').AuditLogResource[] | null
  >(null)
  const audit = useCompanyAudit({ entity_type: recordType, entity_id: recordId }, { enabled: open })
  const rows = audit.data?.pages.flatMap((page) => page.rows) ?? []
  const auditHref = `/audit?entity_type=${encodeURIComponent(recordType)}&entity_id=${encodeURIComponent(recordId)}`
  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)} data-testid="history-trigger">
        <HistoryIcon aria-hidden="true" />
        {triggerLabel ?? t('recordHistory.trigger')}
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="end"
          closeLabel={t('common:action.cancel')}
          data-testid="record-history-panel"
        >
          <SheetHeader>
            <SheetTitle>{title ?? t('recordHistory.title')}</SheetTitle>
            <SheetDescription>{t('recordHistory.description')}</SheetDescription>
          </SheetHeader>
          <SheetBody className="space-y-4">
            {audit.isPending ? (
              <div className="grid gap-3" aria-busy="true">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-24 animate-pulse rounded-lg border border-border bg-muted/40"
                  />
                ))}
              </div>
            ) : audit.isError ? (
              <Alert severity="danger">
                <AlertTitle>{t('companyAudit.errorTitle')}</AlertTitle>
                <AlertDescription>
                  {audit.error.message}
                  <Button variant="outline" onClick={() => void audit.refetch()}>
                    {t('common:action.retry')}
                  </Button>
                </AlertDescription>
              </Alert>
            ) : rows.length === 0 ? (
              <EmptyState title={t('recordHistory.empty')} />
            ) : (
              <div className="grid gap-3">
                {groupAuditRows(rows).map((group) => (
                  <AuditCard
                    key={group.id}
                    rows={group.rows}
                    onOpen={() => setSelected(group.rows)}
                  />
                ))}
              </div>
            )}
            {audit.hasNextPage ? (
              <Button
                variant="outline"
                className="w-full"
                loading={audit.isFetchingNextPage}
                onClick={() => void audit.fetchNextPage()}
              >
                {t('companyAudit.loadMore')}
              </Button>
            ) : null}
            <Button asChild variant="outline" className="w-full">
              <a href={auditHref}>{t('recordHistory.openInAudit')}</a>
            </Button>
          </SheetBody>
        </SheetContent>
      </Sheet>
      <AuditDetailSheet
        rows={selected ?? []}
        open={selected !== null}
        onOpenChange={(value) => {
          if (!value) setSelected(null)
        }}
        onHistory={() => {
          window.location.assign(auditHref)
        }}
      />
    </>
  )
}
